import { useState, useEffect, useMemo } from 'react';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import {
  applyAllFilters,
  checkIfFiltersAreActive,
  normalizeUserSuiteFields,
  normalizeUserSuites,
  removeFilterFromActiveFilters,
} from 'src/pages/Contacts/CustomHooks/helpers';
import { deleteContactById, getContacts } from 'src/services/client';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { toast } from 'react-toastify';

export interface IndividualFilter {
  Id?: string;
  name?: string;
}

export type IContactFilters = {
  roles?: string[];
  property?: IndividualFilter[];
  status?: string[];
  email?: string;
  phone?: string;
  dateRange?: {
    startDate: Date | null;
    endDate: Date | null;
  };
  searchTerm?: string;
  name?: string;
  selectedUnit?: IndividualFilter[];
  selectedBuilding?: IndividualFilter[];
  selectedLevel?: IndividualFilter[];
};

export interface IUseContactFiltersReturn {
  contacts: IContact[];
  handleApplyFilters: (filters: IContactFilters) => void;
  resetFilters: () => void;
  activeFilters: IContactFilters;
  isLoading: boolean;
  error: string | null;
  refetchFullContactList: () => Promise<void>;
  onDeleteContactById: (data: any) => void;
  removeSingleActiveFilter: (filterText: any) => void;
  hasActiveFilters: boolean;
  uniqueContactStatuses: string[];
  currentContactById: IContact | null;
  fetchContactById: (contactId: string) => Promise<void>;
  isLoadingContactById: boolean;
  contactByIdError: string | null;
  clearCurrentContact: () => void;
  refetchSingleContactById: (contactId: string) => Promise<void>;
}

export const useContactFilters = (): IUseContactFiltersReturn => {
  const user = useSelector(selectAuthenticatedUser);

  // Full contact list state.
  const [unfilteredContacts, setUnfilteredContacts] = useState<IContact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<IContact[]>([]);
  const [activeFilters, setActiveFilters] = useState<IContactFilters>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Individual contact state.
  const [currentContactById, setCurrentContactById] = useState<IContact | null>(
    null
  );
  const [isLoadingContactById, setIsLoadingContactById] =
    useState<boolean>(false);
  const [contactByIdError, setContactByIdError] = useState<string | null>(null);

  const userRole: string = useMemo(() => {
    if (!user?.roles || !Array.isArray(user.roles) || user.roles.length === 0) {
      return null;
    }

    const foundRoleWrapper: any = user.roles.find((roleWrapper: any) => {
      return roleWrapper?.role?.name;
    });

    return foundRoleWrapper?.role?.name || null;
  }, [user?.roles]);

  // Fetch an individual contact by ID from the API.
  const fetchContactById = async (contactId: string): Promise<void> => {
    if (!contactId || !user?.id) {
      setContactByIdError('Contact ID or User ID is missing');
      return;
    }

    setIsLoadingContactById(true);
    setContactByIdError(null);

    try {
      // Get all contacts from the good endpoint.
      const response: any = await getContacts();
      const responseJson: any = await response.json();

      if (response.status !== 200) {
        throw new Error(
          `Failed to fetch contacts: ${response.status} ${response.statusText}`
        );
      }

      const contactsData: IContact[] = Array.isArray(responseJson)
        ? responseJson
        : [];
      const fullyNormalizedContacts: IContact[] =
        normalizeUserSuites(contactsData);
      const contactsWithCleanFields: IContact[] = normalizeUserSuiteFields(
        fullyNormalizedContacts
      );

      const foundContact: IContact = contactsWithCleanFields.find(
        (contact: IContact) => contact.id === contactId
      );

      if (foundContact) {
        setCurrentContactById(foundContact);
      } else {
        setContactByIdError(`Contact with ID ${contactId} not found`);
        setCurrentContactById(null);
      }
    } catch (err) {
      console.error('Error fetching contact by ID:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch contact';
      setContactByIdError(errorMessage);
      setCurrentContactById(null);
    } finally {
      setIsLoadingContactById(false);
    }
  };

  const clearCurrentContact = () => {
    setCurrentContactById(null);
    setIsLoadingContactById(false);
    setContactByIdError(null);
  };

  // Fetch the full list of contacts from the API.
  const fetchContacts = async () => {
    if (!user.id || !userRole) return;

    setIsLoading(true);
    setError(null);

    try {
      const response: any = await getContacts();
      const responseJson: any = await response.json();
      const contactsData: IContact[] = Array.isArray(responseJson)
        ? responseJson
        : [];

      const fullyNormalizedContacts: IContact[] =
        normalizeUserSuites(contactsData);
      const contactsWithCleanFields: IContact[] = normalizeUserSuiteFields(
        fullyNormalizedContacts
      );

      setUnfilteredContacts(contactsWithCleanFields);

      if (checkIfFiltersAreActive(activeFilters)) {
        const reFilteredContacts: IContact[] = applyAllFilters(
          contactsWithCleanFields,
          activeFilters
        );

        return setFilteredContacts(reFilteredContacts);
      }

      setFilteredContacts(contactsWithCleanFields);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch contacts');
      setUnfilteredContacts([]);
      setFilteredContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const removeContactFromState = (contactId: string) => {
    setUnfilteredContacts((prev: IContact[]) =>
      prev.filter((contact: IContact) => contact.id !== contactId)
    );
    setFilteredContacts((prev: IContact[]) =>
      prev.filter((contact: IContact) => contact.id !== contactId)
    );
  };

  const onDeleteContactById = async (data: any) => {
    if (!data.id || typeof data.id !== 'string') {
      toast.error(' ⓘ Invalid contact ID provided for deletion.', {
        autoClose: 10000,
        position: 'top-center',
      });
      return console.error('Invalid contact ID provided for deletion');
    }

    try {
      const response: any = await deleteContactById(data.id);

      if (response.status !== 200) {
        toast.error(
          `Failed to delete contact. Server responded with: ${response.status} ${response.statusText}`,
          {
            autoClose: 10000,
            position: 'top-center',
          }
        );
        return console.error(
          `Failed to delete contact. Server responded with: ${response.status} ${response.statusText}`
        );
      }

      removeContactFromState(data.id);

      toast.success(' ⓘ The user has been deleted.', {
        autoClose: 10000,
        position: 'top-center',
      });
    } catch (err) {
      console.error(
        'Unexpected error when trying to delete that contact:',
        err
      );
    }
  };

  // Fetch contacts when userId or userRole changes.
  useEffect(() => {
    fetchContacts();
  }, [user?.id, userRole]);

  // Check if any filters are currently active.
  const hasActiveFilters: boolean = useMemo(() => {
    return (
      Object.keys(activeFilters).length > 0 &&
      // Check if there are any non-empty array filters.
      !Object.values(activeFilters).every((val: any) =>
        Array.isArray(val) ? val.length === 0 : !val
      )
    );
  }, [activeFilters]);

  // The actual contacts to expose - filtered if we have filters, otherwise unfiltered
  const contacts: IContact[] = useMemo(() => {
    return hasActiveFilters ? filteredContacts : unfilteredContacts;
  }, [hasActiveFilters, filteredContacts, unfilteredContacts]);

  // Apply filters to contact data.
  const handleApplyFilters = (filters: IContactFilters) => {
    setActiveFilters(filters);

    const updatedContactsResult: IContact[] = applyAllFilters(
      unfilteredContacts,
      filters
    );

    setFilteredContacts(updatedContactsResult);
  };

  // Reset all filters.
  const resetFilters = () => {
    setActiveFilters({});
    setFilteredContacts(unfilteredContacts);
  };

  // TODO: Finish implementing this logic from https://app.asana.com/1/41213529744312/project/1208712340140532/task/1210618856822411?focus=true
  const removeSingleActiveFilter = (filterToRemove: string) => {
    if (!filterToRemove || filterToRemove.trim() === '') {
      toast.error('No filter value provided to remove', {
        autoClose: 10000,
        position: 'top-center',
      });
      return console.error('No filter value provided to remove');
    }

    const updatedActiveFilters: IContactFilters = removeFilterFromActiveFilters(
      activeFilters,
      filterToRemove
    );

    const hasRemainingActiveFilters: boolean =
      checkIfFiltersAreActive(updatedActiveFilters);

    if (!hasRemainingActiveFilters) {
      setActiveFilters({});
      setFilteredContacts(unfilteredContacts);
      return;
    }

    const newFilteredContacts: IContact[] = applyAllFilters(
      unfilteredContacts,
      updatedActiveFilters
    );

    setActiveFilters(updatedActiveFilters);
    setFilteredContacts(newFilteredContacts);
  };

  const refetchFullContactList = async () => {
    await fetchContacts();
  };

  const refetchSingleContactById = async (contactId: string) => {
    await fetchContactById(contactId);
  };

  const uniqueContactStatuses: string[] = useMemo(() => {
    const uniqueStatuses: string[] = contacts.reduce(
      (accumulator: string[], currentItem: IContact) => {
        const status: string = currentItem.status;

        if (!accumulator.includes(status)) {
          accumulator.push(status);
        }

        return accumulator;
      },
      []
    );

    return uniqueStatuses;
  }, [contacts]);

  return {
    contacts,
    handleApplyFilters,
    resetFilters,
    activeFilters,
    isLoading,
    error,
    refetchFullContactList,
    onDeleteContactById,
    removeSingleActiveFilter,
    hasActiveFilters,
    uniqueContactStatuses,
    currentContactById,
    fetchContactById,
    isLoadingContactById,
    contactByIdError,
    clearCurrentContact,
    refetchSingleContactById,
  };
};
